/**
 * useOrbitMetadata
 * Fetches m1 prompts from the DB-backed admin API.
 * Falls back to the hardcoded arrays in indie-chat.types.tsx if the API fails.
 *
 * Note: Action Card Templates feature has been retired. Action cards now use
 * the hardcoded fallback values only.
 */
import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import OrbitMetadataService from '../services/OrbitMetadataService';
import {
  Prompt,
  ActionCard,
  m1Prompts as fallbackPrompts,
  defaultActionCards as fallbackCards,
} from '../components/orbit/indie-chat.types';

// ─── Icon renderer ────────────────────────────────────────────────────────────
// Keeps the exact SVG paths from the Figma design, parameterised by stroke colour.

function renderPromptIcon(iconName: string, strokeColor: string): React.ReactNode {
  switch (iconName) {
    case 'warning':
      return React.createElement('svg', { xmlns: 'http://www.w3.org/2000/svg', width: '13', height: '13', viewBox: '0 0 13 13', fill: 'none' },
        React.createElement('path', { d: 'M2.16656 7.58344C2.06406 7.58379 1.96356 7.55505 1.87674 7.50056C1.78993 7.44606 1.72035 7.36805 1.67611 7.27559C1.63186 7.18313 1.61476 7.08001 1.62679 6.97822C1.63882 6.87642 1.67948 6.78013 1.74406 6.70053L7.10656 1.17553C7.14679 1.12909 7.2016 1.09772 7.26201 1.08655C7.32242 1.07538 7.38483 1.08507 7.439 1.11405C7.49317 1.14302 7.53588 1.18955 7.56012 1.246C7.58436 1.30245 7.58869 1.36546 7.57239 1.42469L6.53239 4.68553C6.50173 4.7676 6.49143 4.85589 6.50238 4.94282C6.51333 5.02975 6.54521 5.11273 6.59528 5.18463C6.64534 5.25654 6.7121 5.31522 6.78984 5.35565C6.86757 5.39609 6.95394 5.41706 7.04156 5.41678H10.8332C10.9357 5.41643 11.0362 5.44517 11.123 5.49966C11.2099 5.55416 11.2794 5.63217 11.3237 5.72463C11.3679 5.81709 11.385 5.92021 11.373 6.022C11.361 6.1238 11.3203 6.22009 11.2557 6.29969L5.89323 11.8247C5.853 11.8711 5.79819 11.9025 5.73778 11.9137C5.67737 11.9248 5.61496 11.9151 5.56079 11.8862C5.50662 11.8572 5.46391 11.8107 5.43967 11.7542C5.41543 11.6978 5.4111 11.6348 5.42739 11.5755L6.46739 8.31469C6.49806 8.23262 6.50836 8.14433 6.49741 8.0574C6.48646 7.97047 6.45458 7.88749 6.40451 7.81559C6.35445 7.74368 6.28768 7.685 6.20995 7.64456C6.13222 7.60413 6.04584 7.58316 5.95823 7.58344H2.16656Z', stroke: strokeColor || '#B42318', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
      );
    case 'shopping':
      return React.createElement('svg', { xmlns: 'http://www.w3.org/2000/svg', width: '13', height: '13', viewBox: '0 0 13 13', fill: 'none' },
        React.createElement('path', { d: 'M3.25 1.0835L1.625 3.25016V10.8335C1.625 11.1208 1.73914 11.3964 1.9423 11.5995C2.14547 11.8027 2.42102 11.9168 2.70833 11.9168H10.2917C10.579 11.9168 10.8545 11.8027 11.0577 11.5995C11.2609 11.3964 11.375 11.1208 11.375 10.8335V3.25016L9.75 1.0835H3.25Z', stroke: strokeColor || '#B54708', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        React.createElement('path', { d: 'M1.625 3.25H11.375', stroke: strokeColor || '#B54708', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        React.createElement('path', { d: 'M8.66732 5.4165C8.66732 5.99114 8.43904 6.54224 8.03272 6.94857C7.62639 7.3549 7.07529 7.58317 6.50065 7.58317C5.92602 7.58317 5.37492 7.3549 4.96859 6.94857C4.56226 6.54224 4.33398 5.99114 4.33398 5.4165', stroke: strokeColor || '#B54708', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
      );
    case 'benchmark':
    case 'list':
      return React.createElement('svg', { xmlns: 'http://www.w3.org/2000/svg', width: '13', height: '13', viewBox: '0 0 13 13', fill: 'none' },
        React.createElement('path', { d: 'M9.75 10.8332V5.4165', stroke: strokeColor || '#6366F1', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        React.createElement('path', { d: 'M6.5 10.8332V2.1665', stroke: strokeColor || '#6366F1', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        React.createElement('path', { d: 'M3.25 10.8335V7.5835', stroke: strokeColor || '#6366F1', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
      );
    case 'location':
      return React.createElement('svg', { xmlns: 'http://www.w3.org/2000/svg', width: '13', height: '13', viewBox: '0 0 13 13', fill: 'none' },
        React.createElement('path', { d: 'M10.8333 5.41659C10.8333 8.1211 7.83303 10.9378 6.82553 11.8078C6.73218 11.8784 6.61814 11.9166 6.5 11.9166C6.38186 11.9166 6.26782 11.8784 6.17448 11.8078C5.16698 10.9378 2.16667 8.1211 2.16667 5.41659C2.16667 4.26732 2.62321 3.16512 3.43586 2.35247C4.24851 1.53982 5.35073 1.08325 6.5 1.08325C7.64927 1.08325 8.75149 1.53982 9.56414 2.35247C10.3768 3.16512 10.8333 4.26732 10.8333 5.41659Z', stroke: strokeColor || '#0086C9', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        React.createElement('path', { d: 'M6.5 7.04175C7.39746 7.04175 8.125 6.31421 8.125 5.41675C8.125 4.51929 7.39746 3.79175 6.5 3.79175C5.60254 3.79175 4.875 4.51929 4.875 5.41675C4.875 6.31421 5.60254 7.04175 6.5 7.04175Z', stroke: strokeColor || '#0086C9', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
      );
    case 'chart':
      return React.createElement('svg', { xmlns: 'http://www.w3.org/2000/svg', width: '13', height: '13', viewBox: '0 0 13 13', fill: 'none' },
        React.createElement('path', { d: 'M11.9173 3.7915L7.31315 8.39567L4.60482 5.68734L1.08398 9.20817', stroke: strokeColor || '#2AAA62', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
        React.createElement('path', { d: 'M8.66602 3.7915H11.916V7.0415', stroke: strokeColor || '#2AAA62', strokeWidth: '1.08333', strokeLinecap: 'round', strokeLinejoin: 'round' }),
      );
    default:
      return React.createElement('svg', { width: '13', height: '13', viewBox: '0 0 13 13', fill: 'none' },
        React.createElement('circle', { cx: '6.5', cy: '6.5', r: '4', stroke: strokeColor || '#6366F1', strokeWidth: '1.08333' }),
      );
  }
}

// ─── DB record shapes (what the API returns) ──────────────────────────────────

interface DbPrompt {
  id: number;
  prompt_type: string;
  label: string;
  icon_bg_color: string | null;
  icon_name: string | null;
  icon_stroke_color: string | null;
  sort_order: number;
  is_active: number | boolean;
}

interface DbActionCard {
  id: number;
  title: string;
  subtitle_template: string | null;
  badge_label: string | null;
  badge_style: string | null;
  sort_order: number;
  is_active: number | boolean;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapDbPrompt(p: DbPrompt, index: number): Prompt {
  return {
    id: p.id,
    label: p.label,
    iconBg: p.icon_bg_color ?? '#F9F5FF',
    icon: renderPromptIcon(p.icon_name ?? '', p.icon_stroke_color ?? '#6941C6'),
    iconName: p.icon_name ?? null,
    iconStrokeColor: p.icon_stroke_color ?? '#6941C6',
  };
}

function mapDbActionCard(c: DbActionCard, index: number): ActionCard {
  return {
    num: index + 1,
    title: c.title,
    subtitle: c.subtitle_template ?? '',
    badge: c.badge_label ?? '',
    badgeStyle: (c.badge_style as ActionCard['badgeStyle']) ?? 'amber',
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface OrbitMetadata {
  m1Prompts: Prompt[];
  clientPrompts: Prompt[];
  actionCards: ActionCard[];
  loading: boolean;
  refreshingCards: boolean;
  refreshActionCards: () => Promise<void>;
}

export function useOrbitMetadata(clientId?: number): OrbitMetadata {
  const [m1Prompts, setM1Prompts] = useState<Prompt[]>(fallbackPrompts);
  const [clientPrompts, setClientPrompts] = useState<Prompt[]>([]);
  const [actionCards, setActionCards] = useState<ActionCard[]>(fallbackCards); // Static fallback only
  const [loading, setLoading] = useState(true);
  const [refreshingCards, setRefreshingCards] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const requests: Promise<any>[] = [
          OrbitMetadataService.listPrompts({ prompt_type: 'm1' }),
        ];

        if (clientId && clientId > 0) {
          requests.push(
            OrbitMetadataService.listPrompts({ prompt_type: 'client', client_id: clientId })
          );
        }

        const [promptsRes, clientPromptsRes] = await Promise.all(requests);

        if (cancelled) return;

        if (promptsRes?.data?.length) setM1Prompts(promptsRes.data.map(mapDbPrompt));
        if (clientPromptsRes?.data?.length) setClientPrompts(clientPromptsRes.data.map(mapDbPrompt));
      } catch {
        // Silently fall back to hardcoded values
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [clientId]);

  // Action cards are now static; refresh is a no-op for backwards compatibility
  const refreshActionCards = useCallback(async () => {
    // No-op: action cards are now hardcoded fallback values only
  }, []);

  return { m1Prompts, clientPrompts, actionCards, loading, refreshingCards, refreshActionCards };
}